import { Loader2, DollarSign } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { NumericInput } from "../../ui/numeric-input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { formatCurrency } from "../utils";

/**
 * Dialog for recording a payment against a billing
 */
const PaymentDialog = ({
  open,
  onOpenChange,
  selectedBilling,
  paymentForm,
  onPaymentFormChange,
  onSubmit,
  isSubmitting,
}) => {
  const handleFieldChange = (field, value) => {
    onPaymentFormChange({ ...paymentForm, [field]: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Payment - App #{selectedBilling?.billing_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Amount Due</Label>
              <p className="text-lg font-mono font-medium">
                {formatCurrency((selectedBilling?.current_payment_due || selectedBilling?.amount || 0))}
              </p>
            </div>
            <div>
              <Label>Previously Received</Label>
              <p className="text-lg font-mono text-green-600">
                {formatCurrency(selectedBilling?.amount_received || 0)}
              </p>
            </div>
          </div>
          
          <div className="form-group">
            <Label htmlFor="payment-amount">Payment Amount *</Label>
            <NumericInput
              id="payment-amount"
              decimals={2}
              min={0}
              value={paymentForm.amount}
              onChange={(e) => handleFieldChange("amount", e.target.value)}
              placeholder="0.00"
              data-testid="payment-amount-input"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="payment-date">Payment Date *</Label>
            <Input
              id="payment-date"
              type="date"
              value={paymentForm.date}
              onChange={(e) => handleFieldChange("date", e.target.value)}
              data-testid="payment-date-input"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="payment-method">Payment Method</Label>
            <Select 
              value={paymentForm.method} 
              onValueChange={(v) => handleFieldChange("method", v)}
            >
              <SelectTrigger data-testid="payment-method-select">
                <SelectValue placeholder="Select method..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="ach">ACH</SelectItem>
                <SelectItem value="wire">Wire Transfer</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="form-group">
            <Label htmlFor="payment-notes">Notes</Label>
            <Textarea
              id="payment-notes"
              value={paymentForm.notes}
              onChange={(e) => handleFieldChange("notes", e.target.value)}
              placeholder="Check #, reference number, etc."
              rows={2}
              data-testid="payment-notes-input"
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={onSubmit}
              disabled={isSubmitting || !paymentForm.amount || !paymentForm.date}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-payment-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <DollarSign className="w-4 h-4 mr-2" />
              )}
              Record Payment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
